import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaHome, FaExpand, FaCompress, FaArrowLeft, FaArrowRight, FaStar } from 'react-icons/fa';
import { LuSparkles } from 'react-icons/lu';
import { Document, Page, pdfjs } from 'react-pdf';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

// Configure PDF.js worker - use local worker file to avoid CORS issues
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf-worker/pdf.worker.min.js';

function StorybookViewerPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // Single PDF state
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPurchased, setIsPurchased] = useState(false);
  const [storybookId, setStorybookId] = useState(null);
  
  // PDF viewer state
  const [scale, setScale] = useState(1.2);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageTransition, setPageTransition] = useState(''); // 'next' or 'prev' for animation direction
  const [showCover, setShowCover] = useState(true); // Show cover page initially
  const [storyPage, setStoryPage] = useState(1); // Current story page number (1-based)

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) {
      return;
    }
    loadStorybook();
  }, [slug, user, authLoading]);

  // Load saved page position on mount
  useEffect(() => {
    if (storybookId && numPages) {
      const savedStoryPage = localStorage.getItem(`storybook_${storybookId}_page`);
      const savedShowCover = localStorage.getItem(`storybook_${storybookId}_showCover`);
      
      if (savedStoryPage) {
        const pageNum = parseInt(savedStoryPage, 10);
        const totalStoryPages = numPages * 2;
        if (pageNum >= 1 && pageNum <= totalStoryPages) {
          setStoryPage(pageNum);
          setPageNumber(Math.ceil(pageNum / 2));
          setShowCover(savedShowCover === 'true');
        }
      }
    }
  }, [storybookId, numPages]);

  // Save current page to localStorage whenever it changes
  useEffect(() => {
    if (storybookId && numPages) {
      localStorage.setItem(`storybook_${storybookId}_page`, storyPage.toString());
      localStorage.setItem(`storybook_${storybookId}_showCover`, showCover.toString());
    }
  }, [storyPage, showCover, storybookId, numPages]);

  const loadStorybook = async () => {
    try {
      setLoading(true);
      
      // Check if user is logged in
      if (!user) {
        navigate('/login', { state: { from: `/storybook/${slug}` } });
        return;
      }

      // For now, we'll hardcode the kasbah-key storybook as an example
      if (slug === 'kasbah-key') {
        setStorybookId(1);
        
        // Check if user has purchased this storybook
        try {
          const purchased = await apiService.checkStorybookPurchase(1);
          setIsPurchased(purchased);
          
          if (!purchased) {
            setError('يجب شراء القصة للوصول إلى المحتوى الكامل');
            setLoading(false);
            return;
          }
          
          setPdfUrl('/storybooks/kasbah-key/kasbah-key.pdf');
        } catch (err) {
          console.error('Error checking purchase:', err);
          setError('يجب شراء القصة للوصول إلى المحتوى الكامل');
          setLoading(false);
          return;
        }
      } else {
        setError('القصة غير موجودة');
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading storybook:', err);
      setError('فشل تحميل القصة');
    } finally {
      setLoading(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    console.error('Error message:', error?.message);
    console.error('Error stack:', error?.stack);
    setError('فشل تحميل ملف PDF');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const goToNextPage = () => {
    // If cover is showing, open the book to pages 1-2
    if (showCover) {
      setPageTransition('open');
      setTimeout(() => {
        setShowCover(false);
        setStoryPage(1);
        setPageNumber(1);
        setTimeout(() => setPageTransition(''), 50);
      }, 400);
      return;
    }
    
    // Navigate to next story page
    const totalStoryPages = numPages * 2;
    if (storyPage < totalStoryPages) {
      setPageTransition('next');
      setTimeout(() => {
        const newStoryPage = storyPage + 1;
        setStoryPage(newStoryPage);
        // Calculate which PDF page to show (each PDF page has 2 story pages)
        setPageNumber(Math.ceil(newStoryPage / 2));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setPageTransition(''), 50);
      }, 300);
    }
  };

  const goToPreviousPage = () => {
    if (storyPage > 1) {
      setPageTransition('prev');
      setTimeout(() => {
        const newStoryPage = storyPage - 1;
        setStoryPage(newStoryPage);
        // Calculate which PDF page to show
        setPageNumber(Math.ceil(newStoryPage / 2));
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setPageTransition(''), 50);
      }, 300);
    }
  };

  const zoomIn = () => setScale(prevScale => Math.min(prevScale + 0.2, 2.5));
  const zoomOut = () => setScale(prevScale => Math.max(prevScale - 0.2, 0.6));

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
          <p className="mt-4 text-text text-lg">جاري تحميل القصة...</p>
        </div>
      </div>
    );
  }

  if (error || !pdfUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold mb-4 text-text">{error || 'القصة غير موجودة'}</h2>
          {!isPurchased && storybookId && (
            <>
              <p className="text-gray-600 mb-6">
                هذه القصة متاحة للمشترين فقط. قم بشراء القصة للوصول إلى المحتوى الكامل.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate(`/storybooks`)}
                  className="bg-primary text-white px-6 py-3 rounded-xl font-bold hover:bg-primary-dark transition-all duration-300"
                >
                  اشتري القصة الآن
                </button>
                <Link 
                  to="/storybooks" 
                  className="text-primary hover:underline font-medium"
                >
                  العودة إلى القصص
                </Link>
              </div>
            </>
          )}
          {(!storybookId || isPurchased) && (
            <Link to="/storybooks" className="text-primary hover:underline font-medium">
              العودة إلى القصص
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-md z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <Link 
              to="/storybooks"
              className="text-text hover:text-primary transition-colors flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg"
            >
              <FaHome />
              <span className="hidden md:inline">القصص</span>
            </Link>

            <div className="text-center flex-1 mx-4">
              <h1 className="text-text font-bold text-lg md:text-xl">
                مفتاح القصبة - قصة كاملة
              </h1>
              <div className="text-sm text-text2 mt-1">
                {showCover 
                  ? (numPages > 0 ? `${numPages * 2} صفحة قصة - اضغط التالي للبدء` : 'جاري التحميل...') 
                  : (numPages > 0 ? `صفحة ${storyPage} من ${numPages * 2}` : 'جاري التحميل...')
                }
              </div>
            </div>

            <button
              onClick={toggleFullscreen}
              className="text-text hover:text-primary transition-colors p-2 bg-gray-100 rounded-lg"
              title={isFullscreen ? 'خروج من ملء الشاشة' : 'ملء الشاشة'}
            >
              {isFullscreen ? <FaCompress className="text-xl" /> : <FaExpand className="text-xl" />}
            </button>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Previous Button */}
            <button
              onClick={goToPreviousPage}
              disabled={showCover || storyPage <= 1}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
                (!showCover && storyPage > 1)
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <FaArrowRight />
              <span className="hidden sm:inline">السابق</span>
            </button>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={zoomOut}
                disabled={scale <= 0.6}
                className={`px-3 py-2 rounded-lg font-bold transition-all ${
                  scale > 0.6
                    ? 'bg-gray-100 text-text hover:bg-gray-200'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                −
              </button>
              <span className="text-text font-medium min-w-[60px] text-center bg-gray-100 px-3 py-2 rounded-lg text-sm">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                disabled={scale >= 2.5}
                className={`px-3 py-2 rounded-lg font-bold transition-all ${
                  scale < 2.5
                    ? 'bg-gray-100 text-text hover:bg-gray-200'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                +
              </button>
            </div>

            {/* Next Button */}
            <button
              onClick={goToNextPage}
              disabled={!showCover && storyPage >= numPages * 2}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
                (showCover || storyPage < numPages * 2)
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <span className="hidden sm:inline">التالي</span>
              <FaArrowLeft />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Single Page View */}
      <div className="pt-20 pb-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-8">
            <div className="w-full max-w-5xl perspective-1000">
              
              {/* Book Cover - Shows when book is closed */}
              {showCover ? (
                <div 
                  className={`bg-gradient-to-br from-primary via-blue-600 to-indigo-700 shadow-2xl rounded-xl overflow-hidden flex items-center justify-center min-h-[700px] relative ${
                    pageTransition === 'open' ? 'animate-book-open' : 'animate-page-fade-in'
                  }`}
                >
                  <div className="text-center text-white p-12 max-w-2xl">
                    <div className="mb-8">
                      <div className="text-8xl mb-6 animate-bounce-slow">📖</div>
                      <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">
                        مفتاح القصبة
                      </h1>
                      <div className="h-1 w-32 bg-white/50 mx-auto mb-6 rounded-full"></div>
                      <p className="text-2xl mb-2 font-medium">
                        ثلاثة قصص ذهبية
                      </p>
                      <p className="text-xl opacity-90">
                        (الفصل الثالث والأخير)
                      </p>
                    </div>
                    
                    <div className="mt-12 space-y-3">
                      <p className="text-lg opacity-80">By Allemni - علمني</p>
                      {numPages > 0 && (
                        <p className="text-md opacity-70">{numPages} صفحة</p>
                      )}
                    </div>

                    {/* Decorative elements */}
                    <div className="absolute top-8 left-8 text-white/20">
                      <FaStar className="text-6xl" />
                    </div>
                    <div className="absolute bottom-8 right-8 text-white/20">
                      <LuSparkles className="text-6xl" />
                    </div>
                    <div className="absolute top-8 right-12 text-5xl opacity-15">🦅</div>
                    <div className="absolute bottom-12 left-12 text-5xl opacity-15">🏔️</div>
                  </div>

                  {/* Click prompt */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
                    <div className="bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-medium border-2 border-white/30">
                      اضغط "التالي" لفتح القصة 👇
                    </div>
                  </div>
                </div>
              ) : (
                /* PDF Document - Full Spread with Animation */
                <div 
                  className={`bg-white shadow-2xl rounded-xl overflow-hidden flex items-center justify-center min-h-[600px] relative transition-all duration-500 ease-in-out ${
                    pageTransition === 'next' 
                      ? 'animate-page-slide-left' 
                      : pageTransition === 'prev' 
                      ? 'animate-page-slide-right' 
                      : 'animate-page-fade-in'
                  }`}
                  style={{
                    transform: pageTransition ? 'scale(0.95)' : 'scale(1)',
                  }}
                >
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <div className="flex items-center justify-center py-20 bg-gray-100">
                        <div className="text-center">
                          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary mb-4"></div>
                          <p className="text-gray-600">جاري تحميل القصة...</p>
                        </div>
                      </div>
                    }
                    className="flex items-center justify-center"
                  >
                    {numPages && (
                      <Page 
                        key={`page-${pageNumber}`}
                        pageNumber={pageNumber}
                        scale={scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="shadow-md"
                      />
                    )}
                  </Document>
                </div>
              )}
            </div>

            {/* End Message - Only show on last page */}
            {!showCover && storyPage >= numPages * 2 && (
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-6 rounded-xl shadow-lg text-center max-w-2xl w-full animate-bounce-in">
                <h3 className="text-2xl font-bold mb-2">🎉 انتهت القصة</h3>
                <p className="text-white/90 mb-4">نتمنى أنك استمتعت بقراءة "مفتاح القصبة"</p>
                <Link
                  to="/storybooks"
                  className="inline-block bg-white text-green-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all"
                >
                  اكتشف المزيد من القصص
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StorybookViewerPage;
